/**
 * Hub's usage-event catalog.
 *
 * ONE list, and it is a TypeScript union: `recordUsage({ event: "typo" })` does
 * not compile. There is no database enum and no migration to add an event.
 *
 * WHAT BELONGS HERE — and what deliberately does not.
 *
 * Hub already records every WRITE in the entity audit log, with the actor and
 * the channel (`AUDIT_ACTOR_ATTRIBUTION`). Re-recording "created a ticket" here
 * would give us two sources of truth for the same fact and guarantee they
 * eventually disagree. So this catalog is about what the audit log structurally
 * cannot see: READS, and actions that change nothing.
 *
 * That is the whole gap. A client who opens their tickets every Monday and
 * edits nothing produced no row anywhere before this file existed.
 */

import {
  CORE_USAGE_EVENTS,
  defineUsageEvents,
  type UsageEventDefinition,
} from "@west-tennessee-consulting/usage-analytics";

const HUB_USAGE_EVENTS = [
  {
    name: "portal_opened",
    surface: "dashboard",
    description: "Opened the portal",
    actorKinds: ["client"],
  },
  {
    name: "ticket_list_viewed",
    surface: "tickets",
    description: "Looked at their tickets",
  },
  {
    name: "ticket_viewed",
    surface: "tickets",
    description: "Opened a ticket",
  },
  {
    name: "invoice_viewed",
    surface: "invoices",
    description: "Opened an invoice",
  },
  {
    name: "document_viewed",
    surface: "documents",
    description: "Opened a document",
  },
  {
    name: "meridian_message_sent",
    surface: "chat",
    description: "Asked Meridian something",
  },
  {
    name: "time_entries_viewed",
    surface: "time-tracking",
    description: "Reviewed logged time",
  },
  {
    name: "booking_started",
    surface: "booking",
    description: "Started booking a call",
  },
] as const satisfies readonly UsageEventDefinition[];

export const HUB_USAGE_CATALOG = defineUsageEvents([
  ...CORE_USAGE_EVENTS,
  ...HUB_USAGE_EVENTS,
]);

export type HubUsageEventName = (typeof HUB_USAGE_CATALOG)["names"][number];

/** The app key stamped on every row. Several apps may share one Supabase project. */
export const HUB_USAGE_APP = "hub";
